/* eslint-disable import/no-cycle */
import React, { useEffect } from 'react'

import { useFormik } from 'formik'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { clearTicketRefund } from 'actions/common/ticketRefund'
import ErrorImage from 'assets/error-desktop.svg'
import WaitingImage from 'assets/waiting-image.svg'
import Button from 'components/button'
import { Checkbox } from 'components/checkbox'
import datepicker from 'components/datepicker/date-picker-full-responsive'
import dropdown from 'components/dropdown'
import { InputContainer, InputRow } from 'components/forms/styled'
import input from 'components/input'
import TransitionLoader from 'components/loaders/TransitionLoader'
import PageWithBanner from 'components/page-with-banner'
import { loadRecaptcha, ReCaptcha } from 'components/recaptchaV2'
import StatusMessage from 'components/status-message'
import { StyledLabel } from 'components/styled-input-label'
import FORMATS from 'constants/formats'
import MODAL_NAMES from 'constants/modalNames'
import BANNER_IMAGE from 'containers/refund-form/assets/refund-banner.png'
import InformationComponent from 'containers/refund-form/page-information'
import e2eClasses from 'helpers/automation'
import { getDateWithFormat } from 'helpers/dates'
import { useDebouncedScrollEffect } from 'hooks/useScrollToClass'
import NAMESPACES from 'localization/constants/namespaces'
import { parseToGeneralLanguage } from 'localization/helpers'
import {
  getTicketRefundError,
  getTicketRefundSuccess,
  isFetchingTicketRefund
} from 'selectors/common/ticketRefund'
import { getValidateRecaptchaIsFetching } from 'selectors/reCaptcha'

import { validationSchema } from './validation-schema'
import { getFormError } from '../../hocs'
import Textarea from '../../text-area'
import { getRefundReasons } from '../helpers'
import { withRefundFormProps } from '../hocs'
import {
  FormContainer,
  Form,
  LabelGray,
  SeparatorVertical,
  Link,
  SubmitButtonContainer,
  ExtraInformation,
  InformationWarning,
  RefundResultWrapper
} from '../styled'

const Input = withRefundFormProps(input)
const DatePickerFullResponsive = withRefundFormProps(datepicker)
const TextArea = withRefundFormProps(Textarea)
const Dropdown = withRefundFormProps(dropdown)

const InternationalRefundForm = ({
  isLoading,
  isFetchingRecaptchaValidation,
  refundSuccess,
  refundError,
  clearTicketRefund,
  forceLanguage,
  hideRecaptcha,
  hideTermsAndConditions,
  requestRefund
}) => {
  const {
    t,
    i18n: { language }
  } = useTranslation(NAMESPACES.INTERNATIONAL_REFUND_FORM)

  const recaptchaRef = React.createRef()
  const selectedLanguage = parseToGeneralLanguage(forceLanguage || language)
  const PHONE_NUMBER = t('international-refund-form.disclaimer-contact-link-whatsapp-number')

  const getInitialValues = () => ({
    fullNames: [''],
    ticketNumbers: [''],
    reservationCode: '',
    flightDate: undefined,
    reasonForReturn: '',
    sectionReturn: '',
    nameOfPayer: '',
    placeOfResidence: '',
    fullTicket: false,
    email: '',
    phone: '',
    observations: '',
    termsAndConditions: false,
    locale: forceLanguage
  })

  const handleSubmit = values => {
    delete values.termsAndConditions
    requestRefund(values)
  }

  const formikBag = useFormik({
    initialValues: getInitialValues(),
    validationSchema: validationSchema(t),
    onSubmit: handleSubmit
  })

  //Need spread to force effect call
  useDebouncedScrollEffect('.error', [{ ...formikBag.errors }])

  useEffect(() => {
    loadRecaptcha(selectedLanguage)
  }, [])

  const { handleBlur, handleChange, values, touched, errors, setFieldValue } = formikBag

  const refundResponse = refundError || refundSuccess
  if (refundResponse) {
    const statusType = refundError ? 'error' : 'success'
    const buttonLabel = t(`international-refund-form.result-${statusType}.button`)
    const title = t(`international-refund-form.result-${statusType}.title`)
    const description = t(`international-refund-form.result-${statusType}.description`)
    const srcImage = refundError ? ErrorImage : WaitingImage

    return (
      <RefundResultWrapper>
        <StatusMessage
          onClickHandler={() => {
            clearTicketRefund()
            formikBag.resetForm({ values: getInitialValues() })
          }}
          buttonLabel={buttonLabel}
          title={title}
          description={description}
          srcImage={srcImage}
          bgColor="white"
        />
      </RefundResultWrapper>
    )
  }
  return (
    <PageWithBanner
      e2eClass={e2eClasses('corporate.regretRefund')}
      srcImage={BANNER_IMAGE}
      title={t('international-refund-form.title')}
      imgPosition="5% 5%"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          recaptchaRef.current?.execute()
        }}
      >
        <FormContainer>
          {(isLoading || isFetchingRecaptchaValidation) && <TransitionLoader />}
          {t('international-refund-form.info-warning') && (
            <InformationWarning
              dangerouslySetInnerHTML={{ __html: t('international-refund-form.info-warning') }}
            />
          )}
          {t('international-refund-form.info-text') && (
            <InformationComponent
              refundKey="international-refund-form.info-text.details"
              infoText="international-refund-form.info-text"
              t={t}
            />
          )}
          {t('international-refund-form.info-text.extra') && (
            <ExtraInformation
              dangerouslySetInnerHTML={{ __html: t('international-refund-form.info-text.extra') }}
            />
          )}
          {getFormError(refundError, t, clearTicketRefund)}
          <InputRow>
            <InputContainer e2eClass={e2eClasses('corporate.fullNames')} flexDirection="column">
              <Input
                name="fullNames"
                e2eClass={e2eClasses('corporate.fullNames')}
                label={t('international-refund-form.full-name.label')}
                placeholder={t('international-refund-form.full-name.placeholder')}
                value={values.fullNames}
                onChange={e => {
                  formikBag.setFieldValue('fullNames', [e.target.value])
                }}
                onBlur={handleBlur}
                error={touched.fullNames && errors.fullNames}
              />
            </InputContainer>
            <InputContainer e2eClass={e2eClasses('corporate.ticketNumbers')} flexDirection="column">
              <Input
                name="ticketNumbers"
                e2eClass={e2eClasses('corporate.ticketNumbers')}
                label={t('international-refund-form.ticket-numbers.label')}
                placeholder={t('international-refund-form.ticket-numbers.placeholder')}
                value={values.ticketNumbers}
                onChange={e => {
                  formikBag.setFieldValue('ticketNumbers', [e.target.value])
                }}
                onBlur={handleBlur}
                error={touched.ticketNumbers && errors.ticketNumbers}
              />
            </InputContainer>
            <InputContainer
              e2eClass={e2eClasses('corporate.reservationCode')}
              flexDirection="column"
            >
              <Input
                name="reservationCode"
                e2eClass={e2eClasses('corporate.reservationCode')}
                label={t('international-refund-form.reservation-code.label')}
                placeholder={t('international-refund-form.reservation-code.placeholder')}
                value={values.reservationCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.reservationCode && errors.reservationCode}
              />
              <LabelGray e2eClass={e2eClasses('corporate.reservationCode')}>
                {t('international-refund-form.reservation-code')}
              </LabelGray>
            </InputContainer>
          </InputRow>
          <InputRow>
            <InputContainer e2eClass={e2eClasses('corporate.flightDate')} flexDirection="column">
              <StyledLabel e2eClass={e2eClasses('corporate.flightDate')}>
                {t('international-refund-form.flight-date')}
              </StyledLabel>
              <DatePickerFullResponsive
                name="flightDate"
                minDate={new Date('1900-01-01')}
                initialMonth={new Date()}
                modalName={MODAL_NAMES.REFUND_FORM_DATE_PICKER_MOBILE}
                flatStyle
                hideTitleOnDesktop
                onBlur={() => formikBag.setFieldTouched('flightDate', true)}
                error={touched.flightDate && errors.flightDate}
                onChange={date => {
                  const isValidDate = moment(date, FORMATS.ISO_WITHOUT_MILLISECONDS, true).isValid()
                  if (isValidDate) {
                    setFieldValue(
                      'flightDate',
                      getDateWithFormat(date, FORMATS.ISO_WITHOUT_MILLISECONDS)
                    )
                  } else {
                    setFieldValue('flightDate', 'invalid')
                  }
                }}
                langcode={selectedLanguage}
              />
            </InputContainer>
            <InputContainer
              e2eClass={e2eClasses('corporate.reasonForReturn')}
              flexDirection="column"
            >
              <StyledLabel e2eClass={e2eClasses('corporate.reasonForReturn')}>
                {t('international-refund-form.refund-reason')}
              </StyledLabel>
              <Dropdown
                name="reasonForReturn"
                e2eClass={e2eClasses('corporate.reasonForReturn')}
                placeholder={t('international-refund-form.reason-for-return.label')}
                options={getRefundReasons(t, true)}
                fontSize="16px"
                noOptionsMessage={t('international-refund-form.reason-for-return.label-no-options')}
                onChange={e => {
                  formikBag.setFieldValue('reasonForReturn', e.selectedOption.value)
                }}
                onBlur={() => formikBag.setFieldTouched('reasonForReturn', true, true)}
                error={touched.reasonForReturn && errors.reasonForReturn}
              />
            </InputContainer>
            <InputContainer e2eClass={e2eClasses('corporate.sectionReturn')} flexDirection="column">
              <Input
                name="sectionReturn"
                e2eClass={e2eClasses('corporate.sectionReturn')}
                label={t('international-refund-form.section-return.label')}
                placeholder={t('international-refund-form.section-return.placeholder')}
                value={values.sectionReturn}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.sectionReturn && errors.sectionReturn}
                disabled={values.fullTicket}
              />
              <Checkbox
                name="fullTicket"
                e2eClass={e2eClasses('corporate.sectionReturn')}
                small
                onChange={e => {
                  formikBag.setFieldValue('fullTicket', e.checked)
                }}
                checked={formikBag.values.fullTicket}
                error={touched.fullTicket && errors.fullTicket}
              >
                <LabelGray>{t('international-refund-form.full-ticket')}</LabelGray>
              </Checkbox>
            </InputContainer>
          </InputRow>
          <InputRow>
            <InputContainer e2eClass={e2eClasses('corporate.nameOfPayer')}>
              <Input
                name="nameOfPayer"
                e2eClass={e2eClasses('corporate.nameOfPayer')}
                label={t('international-refund-form.payer-name.label')}
                placeholder={t('international-refund-form.payer-name.placeholder')}
                value={values.nameOfPayer}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.nameOfPayer && errors.nameOfPayer}
              />
            </InputContainer>
            <InputContainer e2eClass={e2eClasses('corporate.placeOfResidence')}>
              <Input
                name="placeOfResidence"
                e2eClass={e2eClasses('corporate.placeOfResidence')}
                label={t('international-refund-form.city.label')}
                placeholder={t('international-refund-form.city.placeholder')}
                value={values.placeOfResidence}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.placeOfResidence && errors.placeOfResidence}
              />
            </InputContainer>
            <InputContainer e2eClass={e2eClasses('corporate.email')}>
              <Input
                name="email"
                e2eClass={e2eClasses('corporate.email')}
                label={t('international-refund-form.email.label')}
                placeholder={t('international-refund-form.email.placeholder')}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && errors.email}
              />
            </InputContainer>
          </InputRow>
          <InputRow>
            <InputContainer e2eClass={e2eClasses('corporate.phone')}>
              <Input
                name="phone"
                e2eClass={e2eClasses('corporate.phone')}
                label={t('international-refund-form.phone-number.label')}
                placeholder={t('international-refund-form.phone-number.placeholder')}
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.phone && errors.phone}
              />
            </InputContainer>
          </InputRow>
          <InputRow isLast>
            <InputContainer
              e2eClass={e2eClasses('corporate.observations')}
              flex={1}
              flexDirection="column"
            >
              <TextArea
                name="observations"
                label={t('international-refund-form.observations.label')}
                placeholder={t('international-refund-form.observations.placeholder')}
                value={values.observations}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.observations && errors.observations}
                maxLength={150}
                rows={6}
                pluralKey={t('international-refund-form.observations.remain-characters')}
                singularKey={t('international-refund-form.observations.remain-character')}
              />

              {!hideTermsAndConditions && (
                <React.Fragment>
                  <Checkbox
                    name="termsAndConditions"
                    e2eClass={e2eClasses('corporate.termsAndConditions')}
                    small
                    onChange={e => {
                      formikBag.setFieldValue('termsAndConditions', e.checked)
                    }}
                    refund
                    checked={formikBag.values.termsAndConditions}
                    error={touched.termsAndConditions && errors.termsAndConditions}
                  >
                    <LabelGray>{t('international-refund-form.disclaimer')}</LabelGray>
                    <SeparatorVertical />
                    <LabelGray>
                      {`${t('international-refund-form.disclaimer-contact-text-left')} `}
                      <Link
                        href={`https://api.whatsapp.com/send?phone=${(PHONE_NUMBER || '').trim()}`}
                        target={'_blank'}
                      >{`${t(
                        'international-refund-form.disclaimer-contact-link-whatsapp-text'
                      )}${PHONE_NUMBER}`}</Link>
                      {t('international-refund-form.disclaimer-contact-text-right') &&
                        ` ${t('international-refund-form.disclaimer-contact-text-right')}`}
                      {t('international-refund-form.disclaimer-contact-text-link') && (
                        <Link
                          href={t('international-refund-form.disclaimer-contact-text-link')}
                          target={'_blank'}
                        >
                          {t('international-refund-form.disclaimer-contact-text-link')}
                        </Link>
                      )}
                      {')'}
                    </LabelGray>
                  </Checkbox>
                </React.Fragment>
              )}
            </InputContainer>
          </InputRow>
          {!hideRecaptcha && (
            <InputContainer flex="1" justifyContent="center" margin="30px 0" minHeight="15px">
              <ReCaptcha
                ref={recaptchaRef}
                onSuccess={() => formikBag.handleSubmit(formikBag.values)}
              />
            </InputContainer>
          )}
          <InputContainer flex="1" justifyContent="center">
            <SubmitButtonContainer>
              <Button e2eClass={e2eClasses('corporate.acceptButton')} type="submit">
                {t('international-refund-form.submit-button.label')}
              </Button>
            </SubmitButtonContainer>
          </InputContainer>
        </FormContainer>
      </Form>
    </PageWithBanner>
  )
}

const mapStateToProps = state => ({
  isLoading: isFetchingTicketRefund(state),
  isFetchingRecaptchaValidation: getValidateRecaptchaIsFetching(state),
  refundSuccess: getTicketRefundSuccess(state),
  refundError: getTicketRefundError(state)
})

const mapDispatchToProps = {
  clearTicketRefund
}

InternationalRefundForm.propTypes = {
  clearTicketRefund: PropTypes.func.isRequired,
  languageTag: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isFetchingRecaptchaValidation: PropTypes.bool,
  refundSuccess: PropTypes.object,
  refundError: PropTypes.object,
  hideRecaptcha: PropTypes.bool,
  hideTermsAndConditions: PropTypes.bool,
  informationComponent: PropTypes.node
}

export default connect(mapStateToProps, mapDispatchToProps)(InternationalRefundForm)
